import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n/LanguageProvider";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { AppStoreButton, scrollToId } from "./AppButtons";

export function Header() {
  const { t, locale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = location.pathname === `/${locale}` || location.pathname === `/${locale}/`;

  const goSection = (id) => {
    setMobileOpen(false);
    if (onHome) scrollToId(id);
    else navigate(`/${locale}#${id}`);
  };

  const links = [
    { id: "features", label: t("nav.features") },
    { id: "pricing", label: t("pricing.label") },
    { id: "how", label: t("nav.how") },
    { id: "faq", label: t("nav.faq") },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled ? "border-b border-black/5 bg-white/70 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-container items-center justify-between px-6 md:h-20 md:px-12">
        <Logo height={36} />

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              type="button"
              data-testid={`nav-${l.id}`}
              onClick={() => goSection(l.id)}
              className="focus-ring rounded-full px-4 py-2 text-sm font-medium text-black/70 transition-colors duration-200 hover:text-black"
            >
              {l.label}
            </button>
          ))}
          <Link
            to={`/${locale}/contact`}
            data-testid="nav-contact"
            className="focus-ring rounded-full px-4 py-2 text-sm font-medium text-black/70 transition-colors duration-200 hover:text-black"
          >
            {t("nav.contact")}
          </Link>
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <LanguageSwitcher />
          <div className="hidden md:block">
            <AppStoreButton label={t("nav.download")} testId="header-download-button" className="px-5 py-2.5" />
          </div>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            className="focus-ring ml-1 rounded-full p-2 text-black md:hidden"
          >
            {mobileOpen ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-black/5 bg-white/95 backdrop-blur-xl md:hidden">
          <div className="flex flex-col gap-1 px-6 py-4">
            {links.map((l) => (
              <button
                key={l.id}
                type="button"
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => goSection(l.id)}
                className="rounded-xl px-3 py-3 text-left text-base font-medium text-black/80 hover:bg-black/[0.04]"
              >
                {l.label}
              </button>
            ))}
            <Link
              to={`/${locale}/contact`}
              onClick={() => setMobileOpen(false)}
              className="rounded-xl px-3 py-3 text-base font-medium text-black/80 hover:bg-black/[0.04]"
            >
              {t("nav.contact")}
            </Link>
            <div className="mt-2">
              <AppStoreButton label={t("nav.download")} testId="mobile-download-button" className="w-full justify-center" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
